import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
  profileImage: string;
  isTfSales: boolean;
  isTfAdmin: boolean;
  isPublicAdmin: boolean;
  isSystemAdmin: boolean;
}

interface UserContextType {
  authUser: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  fetchUser: () => Promise<void>;
  isFetchingUser: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [authUser, setUser] = useState<User | null>(() => {
    // 初期値をlocalStorageから取得
    const cachedUser = localStorage.getItem("authUser");
    return cachedUser ? JSON.parse(cachedUser) : null;
  });
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(!authUser); // 初期ロードフラグ

  const fetchUser = async () => {
    setIsFetchingUser(true);
    try {
      const response = await axios.get<User>(
        `${process.env.REACT_APP_BACKEND_URL}/api/user`,
        { withCredentials: true }
      );
      setUser(response.data);
      localStorage.setItem("authUser", JSON.stringify(response.data)); // ローカルにキャッシュ
    } catch (error) {
      console.error("ユーザー情報の取得に失敗しました:", error);
      localStorage.removeItem("authUser"); // エラー時はキャッシュ削除
    } finally {
      setIsFetchingUser(false);
    }
  };

  useEffect(() => {
    if (!authUser) {
      fetchUser();
    }
  }, []); // 初回のみ呼び出し

  return (
    <UserContext.Provider
      value={{ authUser, setUser, fetchUser, isFetchingUser }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
